import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DiscussionCard from '../components/DiscussionCard';
import { discussionService } from '../services/api';
import { Discussion } from '../types';

const StaffOpsCenterPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const canAccess = !!user?.permissions?.includes('access_staff_operations');
  const canViewDrafts = !!user?.permissions?.includes('view_unpublished_discussions');
  const [drafts, setDrafts] = useState<Discussion[]>([]);
  const [published, setPublished] = useState<Discussion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canAccess) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    Promise.all([
      canViewDrafts
        ? discussionService.getAll('draft', 10, 0)
        : Promise.resolve({ data: [] }),
      discussionService.getAll('published', 10, 0),
    ])
      .then(([draftsResponse, publishedResponse]) => {
        setDrafts(draftsResponse.data);
        setPublished(publishedResponse.data);
      })
      .catch(() => setError('Failed to load operations center data'))
      .finally(() => setLoading(false));
  }, [canAccess, canViewDrafts]);

  const hazardFocus = useMemo(() => {
    const counts = new Map<string, number>();
    published.forEach((discussion) => {
      (discussion.hazard_tags || []).forEach((hazard) => {
        counts.set(hazard, (counts.get(hazard) || 0) + 1);
      });
    });

    return Array.from(counts.entries())
      .map(([hazard, count]) => ({ hazard, count }))
      .sort((left, right) => right.count - left.count)
      .slice(0, 5);
  }, [published]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!canAccess) {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <div className="page-shell">
      <div className="container">
        <div className="page-intro">
          <div>
            <p className="eyebrow">Staff operations</p>
            <h1 className="page-heading">Ops center</h1>
            <p className="page-copy">
              One place to review draft workload, recent published output, and hazard emphasis before the next operational handoff.
            </p>
          </div>
          <div className="hero-actions">
            <Link to="/compose" className="btn btn-primary">New discussion</Link>
            <Link to="/staff/drafts" className="btn btn-secondary">Draft queue</Link>
          </div>
        </div>

        {loading && <div className="card">Loading ops center...</div>}
        {error && <div className="alert alert-error">{error}</div>}

        {!loading && !error && (
          <>
            <div className="dashboard-grid archive-summary-grid">
              <div className="module-card">
                <p className="eyebrow">Drafts</p>
                <h3 className="metric-value">{drafts.length}</h3>
                <p className="module-note">Draft products in the latest workload slice</p>
              </div>
              <div className="module-card">
                <p className="eyebrow">Published</p>
                <h3 className="metric-value">{published.length}</h3>
                <p className="module-note">Recent published products loaded</p>
              </div>
            </div>

            <div className="dashboard-grid staff-grid-wide">
              <section className="module-card">
                <div className="module-header">
                  <div>
                    <p className="eyebrow">Hazard mix</p>
                    <h2 className="section-heading">Recent emphasis</h2>
                  </div>
                  <Link to="/archive" className="btn btn-secondary">Archive</Link>
                </div>
                <div className="feature-list">
                  {hazardFocus.length === 0 ? (
                    <span className="module-note">No hazard tags available yet.</span>
                  ) : (
                    hazardFocus.map((hazard) => (
                      <span key={hazard.hazard} className="chip chip-active chip-static">{hazard.hazard} ({hazard.count})</span>
                    ))
                  )}
                </div>
              </section>
            </div>

            <div className="page-section">
              <div className="section-heading-row">
                <h2 className="section-heading">Draft workload</h2>
              </div>
              {drafts.length === 0 ? <div className="card">No drafts in the current slice.</div> : drafts.map((discussion) => <DiscussionCard key={discussion.id} discussion={discussion} />)}
            </div>

            <div className="page-section">
              <div className="section-heading-row">
                <h2 className="section-heading">Recent published products</h2>
              </div>
              {published.length === 0 ? <div className="card">No published products found.</div> : published.slice(0, 4).map((discussion) => <DiscussionCard key={discussion.id} discussion={discussion} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StaffOpsCenterPage;